import { calculatePaymentDates } from './calculatePaymentDates';
import { calculatePaymentDatesFromDocuments } from './calculatePaymentDatesFromDocuments';

type WorkingDayFlag = 'active' | 'inactive';
type PayDate = { date: Date; diffDays: number };

const round2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;

export const calculateAmortizationSimpleFactoring = ({
  creditDetails,
  setAmortizationSchedule,
  workingDayActive,
  documents,
}: {
  creditDetails: {
    amount: number; // neto a girar (V)
    rate: number; // % mensual (ej: 3.3)
    operationDate: string | Date;
    firstPaymentDate?: string | Date;
    operationCost?: number;
    otherExpenses?: number;
    taxes?: number;
    notary?: number;
  };
  setAmortizationSchedule: (rows: any[]) => void;
  workingDayActive: WorkingDayFlag;
  documents?: any[];
}) => {
  // ===== 1) Entradas y gastos fijos =====
  const operationCost = creditDetails.operationCost ?? 0;
  const operationCostIVA = Math.round(operationCost * 0.19) || 0;
  const otherExpenses = creditDetails.otherExpenses ?? 0;
  const taxes = creditDetails.taxes ?? 0;
  const notary = creditDetails.notary ?? 0;

  const fixedFees =
    operationCost + operationCostIVA + otherExpenses + taxes + notary; // F
  const netDesired = creditDetails.amount; // V
  const r = (creditDetails.rate ?? 0) / 100; // tasa mensual en decimal

  // ===== 2) Fechas / períodos (NO devolvemos antes aunque vengan documents) =====
  const operationDate = new Date(creditDetails.operationDate);
  const firstPaymentDate = creditDetails.firstPaymentDate
    ? new Date(creditDetails.firstPaymentDate)
    : documents?.[0]?.paymentDate
      ? new Date(documents[0].paymentDate)
      : undefined;

  let paymentDates: PayDate[] = [];
  if (documents && documents.length > 0) {
    return calculatePaymentDatesFromDocuments(documents, creditDetails);
  } else {
    paymentDates = calculatePaymentDates(
      creditDetails,
      firstPaymentDate!,
      workingDayActive,
      operationDate,
    );
  }

  const n = paymentDates.length;
  if (n <= 0) {
    setAmortizationSchedule([]);
    return;
  }

  // f = r * ((promedio de días)/30)
  const avgDays =
    paymentDates.reduce(
      (acc, p) =>
        acc +
        (p.date.getTime() - new Date(creditDetails.operationDate).getTime()) /
          (1000 * 60 * 60 * 24),
      0,
    ) / n;

  const f = r * (avgDays / 30);
  if (f >= 1) {
    alert('La tasa de interés es demasiado alta');
    return;
  }
  const C = (netDesired + fixedFees) / (1 - f); // <- ESTA es la clave
  let balance = netDesired + fixedFees;

  // ===== 4) Principal fijo por cuota =====
  const cuota = C / n;

  // ===== 5) Cronograma: interés sobre el principal de la cuota (no sobre saldo) =====
  const schedule: any[] = [];
  let totalAmortization = 0;

  paymentDates.forEach((p, idx) => {
    const days =
      (p.date.getTime() - new Date(creditDetails.operationDate).getTime()) /
      (1000 * 60 * 60 * 24);

    // Interés prorrateado por días/30 sobre el principal de la cuota
    const interest = cuota * r * (days / 30);

    // Principal = solo amortización (capital fijo)
    const principal = cuota - interest;

    // Cuota total = principal + interés
    const amount = cuota;
    balance -= principal;
    totalAmortization += principal;

    schedule.push({
      month: idx + 1,
      paymentDate: p.date,
      amount: round2(amount),
      interest: round2(interest),
      principal: round2(principal),
      balance: round2(Math.max(0, balance)),
      operationCost,
      otherExpenses,
      totalAmortization: round2(totalAmortization),
    });
  });

  // (opcional) chequeo de neto
  // const netCheck = C - (totalInterest + fixedFees);

  setAmortizationSchedule(schedule);
};
